import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowUpLeft, ScrollText } from 'lucide-react'
import {
  formatDateTime, formatMoney, formatNumber, MOVEMENT_REASONS, type MovementFilter, type MovementReason,
} from '@najah/core'
import {
  Alert, Badge, EmptyState, Input, Pagination, Select,
  Table, TableSkeleton, TableWrapper, Tbody, Td, Th, Thead, Tr, cn,
} from '@najah/ui'
import { inventoryApi } from '../lib/catalogApi'

/** لون الشارة حسب أثر الحركة: دخول أخضر، خروج أحمر، تسوية محايدة */
const REASON_TONE: Record<MovementReason, 'success' | 'danger' | 'warning' | 'info' | 'neutral'> = {
  INITIAL: 'info',
  PURCHASE: 'success',
  RETURN_IN: 'success',
  TRANSFER_IN: 'success',
  SALE: 'danger',
  RETURN_OUT: 'danger',
  TRANSFER_OUT: 'danger',
  DAMAGE: 'danger',
  ADJUSTMENT: 'warning',
}

export function MovementsPage() {
  const [filter, setFilter] = useState<Partial<MovementFilter>>({ page: 1, pageSize: 50 })

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['inventory', 'movements', filter],
    queryFn: () => inventoryApi.movements(filter),
    placeholderData: keepPreviousData,
  })

  function patch(next: Partial<MovementFilter>) {
    setFilter((f) => ({ ...f, ...next, page: next.page ?? 1 }))
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">سجل حركات المخزون</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            {data ? `${formatNumber(data.total)} حركة` : 'جارٍ التحميل…'}
          </p>
        </div>
        <Link
          to="/inventory"
          className="inline-flex h-10 items-center gap-2 rounded-lg border border-border bg-surface px-4 text-sm font-medium text-fg transition-colors hover:bg-surface-hover"
        >
          نظرة عامة على المخزون
          <ArrowUpLeft className="size-4" />
        </Link>
      </div>

      <Alert tone="info">
        هذا السجل تراكمي: كل سطر يضيف أو يطرح من الرصيد، ولا يُعدَّل ولا يُحذف أي سطر سابق.
        رصيد أي منتج هو ببساطة مجموع حركاته.
      </Alert>

      <div className="grid gap-3 rounded-xl border border-border bg-surface p-4 sm:grid-cols-3">
        <Select
          label="السبب"
          value={filter.reason ?? ''}
          onChange={(e) => patch({ reason: (e.target.value || undefined) as MovementReason | undefined })}
          options={Object.entries(MOVEMENT_REASONS).map(([value, label]) => ({ value, label }))}
          placeholder="كل الأسباب"
        />
        <Input
          label="من تاريخ" type="date" ltr
          value={filter.from ?? ''}
          onChange={(e) => patch({ from: e.target.value || undefined })}
        />
        <Input
          label="إلى تاريخ" type="date" ltr
          value={filter.to ?? ''}
          onChange={(e) => patch({ to: e.target.value || undefined })}
        />
      </div>

      <TableWrapper className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
        <Table>
          <Thead>
            <tr>
              <Th>التاريخ والوقت</Th>
              <Th>المنتج</Th>
              <Th>السبب</Th>
              <Th align="center">الكمية</Th>
              <Th align="end">تكلفة الوحدة</Th>
              <Th>المنفّذ</Th>
              <Th>ملاحظة</Th>
            </tr>
          </Thead>

          {isPending ? (
            <TableSkeleton rows={10} cols={7} />
          ) : (
            <Tbody>
              {data?.items.map((m) => (
                <Tr key={m.id}>
                  <Td className="whitespace-nowrap text-xs text-fg-muted">{formatDateTime(m.occurredAt)}</Td>
                  <Td>
                    <p className="truncate font-medium text-fg">{m.productName}</p>
                    <p className="font-mono text-xs text-fg-subtle" dir="ltr">{m.productSku}</p>
                  </Td>
                  <Td>
                    <Badge tone={REASON_TONE[m.reason]}>{MOVEMENT_REASONS[m.reason]}</Badge>
                  </Td>
                  <Td align="center">
                    <span
                      className={cn('font-semibold tabular-nums', m.qtyDelta > 0 ? 'text-success' : 'text-danger')}
                      dir="ltr"
                    >
                      {m.qtyDelta > 0 ? '+' : ''}{formatNumber(m.qtyDelta)}
                    </span>
                  </Td>
                  <Td align="end" className="tabular-nums text-fg-muted" dir="ltr">
                    {m.unitCost != null ? formatMoney(m.unitCost) : '—'}
                  </Td>
                  <Td className="text-sm text-fg-muted">{m.userName ?? '—'}</Td>
                  <Td className="max-w-xs truncate text-xs text-fg-subtle" title={m.note ?? undefined}>
                    {m.note ?? '—'}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>

        {!isPending && data?.items.length === 0 && (
          <EmptyState
            icon={<ScrollText className="size-10" />}
            title="لا توجد حركات"
            description="تظهر هنا كل عمليات الإدخال والإخراج والتسوية"
          />
        )}

        {data && data.items.length > 0 && (
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            pageSize={data.pageSize}
            onPageChange={(page) => setFilter((f) => ({ ...f, page }))}
            onPageSizeChange={(pageSize) => patch({ pageSize })}
          />
        )}
      </TableWrapper>
    </div>
  )
}
